/**
 * useDefaultPageChatDescriptor — builds the default "page" floor EXTERNAL dock descriptor.
 *
 * The default-page surface renders its chat body LOCALLY (via <DockSlot> +
 * DefaultPageChatBody), so the dock only needs an identity/frame for it — no
 * engine/body config. The descriptor is `external: true` AND `isDefault: true`:
 * it stays the low-priority "floor" (any specific surface outranks it) while the
 * dock renders only a portal target and the consumer portals the body in. `key`
 * churns on `pathname` so the surface re-registers (and the body re-renders) on
 * navigation; `persistKey` stays coarse so dock geometry is stable across pages.
 */

import { useMemo } from 'react';

import { pageSurfaceContext } from '@shared/components/chat/dock/surfaceContext';

import type { ChatSurfaceDescriptor } from '@shared/components/chat/dock/chatDockTypes';

/** Dock owner id for the default page floor — shared by the descriptor and the `<DockSlot ownerId>` so they can't diverge. */
export const DEFAULT_PAGE_DOCK_OWNER_ID = 'page';

/**
 * Build the default page floor's external ChatDock descriptor.
 *
 * @param pathname - The current route path — drives descriptor identity (re-registers on navigation).
 * @returns A memoized identity-only external+isDefault ChatSurfaceDescriptor for the page floor.
 */
export function useDefaultPageChatDescriptor(pathname: string): ChatSurfaceDescriptor {
  return useMemo<ChatSurfaceDescriptor>(
    () => ({
      ownerId: DEFAULT_PAGE_DOCK_OWNER_ID,
      // key encodes pathname so descriptor identity changes on navigation
      // (re-registers → the portaled body re-renders with fresh page context).
      // persistKey stays coarse so dock geometry is stable across pages.
      key: `page:${pathname}`,
      persistKey: 'page',
      // The floor must stay the low-priority fallback: never the active owner,
      // shown only when no specific surface is registered.
      isDefault: true,
      external: true,
      // The floor is about whatever page it sits on: the header chip names it.
      context: pageSurfaceContext(pathname),
    }),
    [pathname],
  );
}
